/*
 * FALLBACK ONLY: reached solely from the legacy branch of firstPass, when no
 * aligner/WhisperX helper answers (phones). Kept working per project
 * constraint — do not extend.
 */

/**
 * Burst-first GROUP segmentation — cut only where the audio offers a boundary.
 *
 * WHY THIS EXISTS
 * ---------------
 * Everything upstream of here tries to answer "where does word N start?".
 * Forensics on the user's 44.8 s Jordanian-Arabic recording says that question
 * has no answer for most of the words in it:
 *
 *   - Gemini transcribes the words well and its timestamps are GENERATED, not
 *     measured. Cuts at real silence gaps come out right; every boundary INSIDE
 *     a continuous burst is an invention that the warp, the DTW and the onset DP
 *     can only make plausible, never correct.
 *   - Word-level cuts of fast connected speech ("انا اسمي امجد") are not merely
 *     hard for the pipeline, they are hard for a human: they produce 0.2 s clips
 *     that nobody — model or person — can recognise on their own.
 *
 * The measured shape of that, from the stored run log of the last full run
 * (73 final segments, isolation-QA pass rate 19%):
 *
 *     clip duration   passed QA alone
 *     < 0.25 s         0 / 30     0%
 *     0.25 - 0.50 s    4 / 26    15%
 *     0.50 - 0.75 s    4 / 10    40%
 *     0.75 - 1.00 s    4 /  5    80%
 *     > 1.00 s         2 /  2   100%
 *
 * Recognisability is a function of DURATION, and the pipeline was spending its
 * entire budget manufacturing clips on the wrong side of that curve.
 *
 * WHAT THIS DOES INSTEAD
 * ----------------------
 * A segment is no longer "one word". It is one GROUP of consecutive words that
 * the audio gives no confident reason to divide.
 *
 *  1. Silence between bursts is a MANDATORY cut. It is the one boundary the
 *     recording actually contains.
 *  2. Inside a burst, a cut is made only at a CONFIDENT acoustic onset — a
 *     spectral-flux peak whose normalised strength clears
 *     {@link ONSET_SPLIT_STRENGTH} and which leaves at least
 *     {@link MIN_GROUP_SEC} of audio on either side.
 *  3. Words with no confident boundary between them stay in ONE segment, whose
 *     label is those words joined in order with spaces. Multi-word labels are
 *     first-class — the human merge feature already produces them.
 *  4. Caps stop a group growing into a sentence: {@link MAX_GROUP_WORDS} words
 *     and {@link MAX_GROUP_SEC} seconds. A cap that cannot be met from confident
 *     evidence is met from the best weaker candidate available, and the fact
 *     that it was forced is recorded per burst.
 *
 * FULL SPEECH COVERAGE, BY CONSTRUCTION
 * -------------------------------------
 * Within a burst the first group starts at the burst start, the last ends at the
 * burst end, and adjacent groups share a boundary value — so there is no
 * micro-gap anywhere and no arithmetic that could introduce one. Between bursts
 * there are no segments at all, which is correct: silence needs no label. The
 * coverage figure to read is therefore the fraction of SPEECH covered, and the
 * only way it can fall below 1 is a burst the first pass gave no words to (a
 * transcript RECALL failure, which is `gapFill`'s job, not a boundary problem).
 *
 * WHAT THIS DELIBERATELY DOES NOT DO
 * ----------------------------------
 * Move a label from one burst to another, for the reasons `onsets.ts` documents
 * at length: nothing here knows WHICH words are in which burst, only what is
 * plausible, and plausible-and-wrong is the failure being removed. Burst
 * membership arrives from `refineBoundaries` and is preserved exactly.
 *
 * DEPENDENCIES
 * ------------
 * Pure functions over plain objects and typed arrays. No DB, no browser, no API
 * — the thresholds below were calibrated by replaying the stored run log offline
 * and they can be re-calibrated the same way.
 */

import { round3 } from '../../lib/num';
import type { RawSegment, SegmentKind } from '../../types';
import {
  detectDips,
  estimateSyllables,
  expectedDurationRange,
} from './onsetAlign';
import type { EnvelopeLike, OnsetCandidate, SpanSec } from '../onsetDetect';

/* ------------------------------------------------------------------ */
/* Configuration                                                       */
/* ------------------------------------------------------------------ */

/** Most words one segment's label may hold before a split is forced. */
export const MAX_GROUP_WORDS = 5;

/** Longest one segment may run before a split is forced, seconds. */
export const MAX_GROUP_SEC = 3.0;

/**
 * How strong a spectral-flux peak must be to be trusted as a word boundary.
 *
 * `strength` is the peak's flux normalised by the file's 98th percentile and
 * clipped to 1, so it is comparable across recordings (see
 * `computeOnsetFlux`). The calibration file yields 375 peaks over 53 words —
 * the detector fires on SYLLABLES, and a threshold is the only thing separating
 * "a new word probably starts here" from "a new vowel starts here".
 *
 * Measured on that file (375 peaks; "usable" = inside a burst with at least
 * {@link MIN_GROUP_SEC} of room on both sides; segment counts at the default
 * floor):
 *
 *     threshold   peaks   usable   segments out   median duration
 *       0.20        374      57         31           0.66 s
 *       0.40        324      48         30           0.69 s
 *       0.50        270      41         30           0.69 s
 *       0.65        184      27         30           0.69 s
 *       0.85        110      19         30           0.69 s
 *       1.00         86      15         30           0.69 s
 *
 * The honest reading of that table is that ON THIS FILE the threshold is not the
 * binding constraint: {@link MIN_GROUP_SEC} and the "at most one cut per word"
 * rule are, and everything from 0.40 upward produces the same segmentation. The
 * threshold only starts to bind when the floor is relaxed — at a 0.25 s floor
 * the same sweep runs 41 segments at 0.20 down to 36 at 0.85.
 *
 * 0.85 is chosen for the middle of that plateau rather than its edge, so a
 * recording with a different flux distribution moves gradually rather than
 * falling off a cliff, and because every peak it keeps is at or near saturation
 * — a genuine burst of broadband energy after a closure, which is what a word
 * onset looks like — while the tail it drops is syllable nuclei inside a word.
 */
export const ONSET_SPLIT_STRENGTH = 0.85;

/**
 * Least audio a group may be left with on either side of an interior split.
 *
 * Not a cosmetic minimum — it is read straight off the pass-rate table in the
 * header, and it is the parameter that actually decides this stage's output.
 * Below 0.25 s nothing was recognisable at all and between 0.25 s and 0.50 s one
 * clip in seven was, so a cut is only worth making when BOTH sides land at or
 * above the 40%-and-rising part of the curve. Swept on the calibration file:
 *
 *     floor    segments   median   under 0.45 s   predicted recognisable
 *     0.25 s      36       0.56 s       16              43%
 *     0.35 s      33       0.62 s        9              51%
 *     0.45 s      30       0.69 s        5              59%
 *     0.55 s      29       0.76 s        5              63%
 *
 * 0.45 rather than 0.55 because past it the curve flattens and the extra
 * coarseness buys almost nothing; the five remaining sub-0.45 s segments are
 * whole SHORT BURSTS, which are mandatory cuts and are not this floor's to fix.
 * A burst too short to be divided this way is simply not divided.
 */
export const MIN_GROUP_SEC = 0.45;

/**
 * Shortest segment this stage will emit, seconds.
 *
 * A burst can be shorter than this on its own (a clipped "لا", a lip smack the
 * VAD kept). Its boundaries are still mandatory cuts, so the segment is widened
 * symmetrically into the surrounding SILENCE rather than dropped or left as a
 * sub-frame sliver — silence on the edges of a clip costs nothing to listen to,
 * and 0.12 s is what the rest of the pipeline (`isPathological`,
 * `minimumDurationFor`) already calls the floor.
 */
export const MIN_EMIT_SEC = 0.12;

export interface GroupingOptions {
  maxWords?: number;
  maxGroupSec?: number;
  minGroupSec?: number;
  /** Confident-onset threshold. See {@link ONSET_SPLIT_STRENGTH}. */
  splitStrength?: number;
}

/* ------------------------------------------------------------------ */
/* Log shapes                                                          */
/* ------------------------------------------------------------------ */

/** Where one interior boundary came from. */
export interface SplitPoint {
  timeSec: number;
  /** Onset strength, 0..1. Zero for an even division. */
  strength: number;
  source: 'flux' | 'dip' | 'even';
  /** True when it cleared {@link ONSET_SPLIT_STRENGTH} on its own merit. */
  confident: boolean;
  /** True when a cap demanded a split and no confident candidate was left. */
  forced: boolean;
}

/** What grouping decided for one burst. The unit of diagnosis. */
export interface BurstGroupLog {
  burstIndex: number;
  startSec: number;
  endSec: number;
  /** Words the first pass placed in this burst. */
  wordsIn: number;
  /** Segments emitted for it. */
  segmentsOut: number;
  /** Flux peaks inside it that cleared the confidence threshold and the margins. */
  confidentOnsets: number;
  /** The boundaries actually used, in time order. */
  splits: SplitPoint[];
  /** Group labels, in time order. */
  labels: string[];
  /** Groups still over a cap because nothing could be split. */
  capViolations: number;
  note?: string;
}

export interface GroupingResult {
  /** The grouped segments, time-ordered and tiling every non-empty burst. */
  segments: RawSegment[];
  bursts: BurstGroupLog[];
  /** Segments whose label holds more than one word. */
  multiWordGroups: number;
  /** Words that ended up sharing a segment with another word. */
  wordsFused: number;
  /** Bursts the first pass gave no words to — the only source of lost coverage. */
  emptyBursts: number;
  /** Interior boundaries taken from a confident onset. */
  confidentSplits: number;
  /** Interior boundaries a cap forced onto weaker evidence. */
  forcedSplits: number;
  /** Groups left over a cap because the burst offered nothing to split on. */
  capViolations: number;
  /** Emitted segments widened into silence to clear {@link MIN_EMIT_SEC}. */
  widenedToFloor: number;
  /**
   * Fraction of ALL boundaries that sit on a burst edge (silence) or a confident
   * onset. 1 by construction unless a cap forced a weaker split; the caller is
   * expected to assert on it.
   */
  boundaryEvidenceFrac: number;
}

/* ------------------------------------------------------------------ */
/* Small shared helpers                                                */
/* ------------------------------------------------------------------ */

/** 0 inside the label's plausible window, growing with relative error outside. */
function durationPenalty(text: string, kind: string, durSec: number): number {
  const { minSec, maxSec } = expectedDurationRange(text, kind);
  if (durSec < minSec) return (minSec - durSec) / minSec;
  if (durSec > maxSec) return (durSec - maxSec) / maxSec;
  return 0;
}

/**
 * The kind a fused group takes.
 *
 * A group containing any real word is a word — "a fused filler joins the group
 * label" is the rule, so "اه بدي" is one `word` tile reading both. Otherwise the
 * most speech-like member wins, which keeps a filler that shared a burst with a
 * click from being demoted to `noise`.
 */
export function groupKind(kinds: readonly SegmentKind[]): SegmentKind {
  if (kinds.length === 0) return 'word';
  const order: SegmentKind[] = ['word', 'unclear', 'filler', 'noise'];
  for (const k of order) if (kinds.includes(k)) return k;
  return kinds[0];
}

/** Words joined in order with single spaces. Empty labels drop out. */
export function joinLabels(parts: readonly string[]): string {
  return parts
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

/* ------------------------------------------------------------------ */
/* 1 — which burst holds which run of words                            */
/* ------------------------------------------------------------------ */

/**
 * Assign each segment to a burst, forced monotone.
 *
 * Identical in behaviour to `partitionByOverlap` in `onsets.ts` — most overlap
 * wins, a segment stranded in silence joins its nearest burst, and the mapping
 * is then made non-decreasing so every burst owns a CONTIGUOUS run. Re-stated
 * here rather than exported from there because that function is private and the
 * concurrent agent imports `onsets.ts`; the two must not drift, so any change to
 * one belongs in both.
 */
export function assignToBursts(
  segments: readonly { startSec: number; endSec: number }[],
  bursts: readonly SpanSec[],
): Array<[from: number, to: number]> {
  const groups: Array<[number, number]> = bursts.map(() => [0, 0]);
  if (bursts.length === 0) return groups;

  const home = segments.map((s) => {
    let best = -1;
    let bestOverlap = 0;
    for (let i = 0; i < bursts.length; i++) {
      const overlap = Math.min(s.endSec, bursts[i].endSec) - Math.max(s.startSec, bursts[i].startSec);
      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        best = i;
      }
    }
    if (best >= 0) return best;
    let nearest = 0;
    let dist = Infinity;
    for (let i = 0; i < bursts.length; i++) {
      const d =
        s.startSec >= bursts[i].endSec ? s.startSec - bursts[i].endSec : bursts[i].startSec - s.endSec;
      if (d < dist) {
        dist = d;
        nearest = i;
      }
    }
    return nearest;
  });

  let cursor = 0;
  const fixed = home.map((h) => {
    const v = Math.max(h, cursor);
    cursor = v;
    return v;
  });
  let i = 0;
  for (let b = 0; b < bursts.length; b++) {
    const from = i;
    while (i < fixed.length && fixed[i] === b) i++;
    groups[b] = [from, i];
  }
  if (i < segments.length) groups[bursts.length - 1][1] = segments.length;
  return groups;
}

/* ------------------------------------------------------------------ */
/* 2 — choosing the split points inside one burst                      */
/* ------------------------------------------------------------------ */

/**
 * Confident onsets inside `(startSec, endSec)`, strongest first, spaced out.
 *
 * Strongest-first rather than left-to-right for the same reason `pickOnsetPeaks`
 * does it: a real word onset must never be suppressed by a weaker peak that
 * happened to come 80 ms earlier.
 */
function pickSplits(
  candidates: readonly OnsetCandidate[],
  startSec: number,
  endSec: number,
  minGroupSec: number,
  maxCount: number,
): OnsetCandidate[] {
  if (maxCount <= 0) return [];
  const usable = candidates
    .filter((c) => c.timeSec >= startSec + minGroupSec && c.timeSec <= endSec - minGroupSec)
    .sort((a, b) => b.strength - a.strength || a.timeSec - b.timeSec);

  const taken: OnsetCandidate[] = [];
  for (const c of usable) {
    if (taken.length >= maxCount) break;
    if (taken.some((t) => Math.abs(t.timeSec - c.timeSec) < minGroupSec)) continue;
    taken.push(c);
  }
  return taken.sort((a, b) => a.timeSec - b.timeSec);
}

/**
 * The best boundary available strictly inside `(a, b)` when a cap demands one.
 *
 * Order of preference is the order of evidence quality: a sub-threshold flux
 * peak (still a real spectral discontinuity, just not a confident one), then an
 * energy dip (could be a stop consonant), then the midpoint (no evidence at
 * all). Whichever is used is recorded, because "the pipeline invented this
 * boundary" is exactly the thing that must never be silent again.
 */
function forcedSplitIn(
  flux: readonly OnsetCandidate[],
  dips: readonly OnsetCandidate[],
  a: number,
  b: number,
  minGroupSec: number,
  used: readonly number[],
): SplitPoint | null {
  const free = (t: number): boolean =>
    t >= a + minGroupSec && t <= b - minGroupSec && !used.some((u) => Math.abs(u - t) < 1e-6);

  const bestOf = (list: readonly OnsetCandidate[]): OnsetCandidate | null => {
    let best: OnsetCandidate | null = null;
    for (const c of list) {
      if (!free(c.timeSec)) continue;
      if (!best || c.strength > best.strength) best = c;
    }
    return best;
  };

  const peak = bestOf(flux);
  if (peak) {
    return { timeSec: peak.timeSec, strength: peak.strength, source: 'flux', confident: false, forced: true };
  }
  const dip = bestOf(dips);
  if (dip) {
    return { timeSec: dip.timeSec, strength: dip.strength, source: 'dip', confident: false, forced: true };
  }
  const mid = (a + b) / 2;
  if (!free(mid)) return null;
  return { timeSec: mid, strength: 0, source: 'even', confident: false, forced: true };
}

/* ------------------------------------------------------------------ */
/* 3 — laying the burst's words onto the chosen groups                 */
/* ------------------------------------------------------------------ */

/** Weight on "the group's span is a plausible length for its label". */
const W_DURATION = 1.6;

/** Weight on "each group got its proportional share of the syllables". */
const W_SHARE = 2.0;

/** Charged per word over {@link MAX_GROUP_WORDS}. Big enough to be a last resort. */
const OVER_CAP_PENALTY = 25;

/**
 * Assign W consecutive words to G consecutive groups, W >= G >= 1.
 *
 * Only the WORD-to-group mapping is decided here; the group boundaries are
 * already fixed by the audio and are never moved. The cost is deliberately not
 * the one `placeSegmentsInBurst` uses: there is no "distance from the model's
 * timestamp" term, because the model's intra-burst timestamps are the thing this
 * whole redesign stopped believing. What is left is (a) does the resulting label
 * plausibly take this long to say, and (b) did each group get its share of the
 * syllables in proportion to the SPEECH time it holds.
 *
 * Returns the exclusive word index each group ends at.
 */
export function assignWordsToGroups(
  labels: readonly string[],
  kinds: readonly SegmentKind[],
  spans: ReadonlyArray<{ startSec: number; endSec: number; speechSec: number }>,
  maxWords: number,
): number[] {
  const W = labels.length;
  const G = spans.length;
  if (G <= 1) return [W];

  const syl = labels.map((l) => Math.max(1, estimateSyllables(l)));
  const cumSyl = new Float64Array(W + 1);
  for (let i = 0; i < W; i++) cumSyl[i + 1] = cumSyl[i] + syl[i];
  const totalSyl = cumSyl[W] || 1;
  let totalSpeech = 0;
  for (const s of spans) totalSpeech += s.speechSec;
  if (totalSpeech <= 0) totalSpeech = 1;

  const cost = (g: number, from: number, to: number): number => {
    const span = spans[g];
    const dur = span.endSec - span.startSec;
    const text = joinLabels(labels.slice(from, to));
    const kind = groupKind(kinds.slice(from, to));
    const share = (cumSyl[to] - cumSyl[from]) / totalSyl;
    const want = span.speechSec / totalSpeech;
    const over = Math.max(0, to - from - maxWords);
    return (
      W_DURATION * durationPenalty(text, kind, dur) +
      W_SHARE * Math.abs(share - want) +
      OVER_CAP_PENALTY * over
    );
  };

  const INF = Number.POSITIVE_INFINITY;
  // dp[g][w] = best cost of putting the first w words into the first g groups.
  const dp: Float64Array[] = [];
  const back: Int32Array[] = [];
  for (let g = 0; g <= G; g++) {
    dp.push(new Float64Array(W + 1).fill(INF));
    back.push(new Int32Array(W + 1).fill(-1));
  }
  dp[0][0] = 0;

  for (let g = 1; g <= G; g++) {
    // Every group takes at least one word, so w is bounded on both sides.
    for (let w = g; w <= W - (G - g); w++) {
      let best = INF;
      let bestPrev = -1;
      for (let w0 = g - 1; w0 < w; w0++) {
        const prev = dp[g - 1][w0];
        if (prev === INF) continue;
        const total = prev + cost(g - 1, w0, w);
        if (total < best) {
          best = total;
          bestPrev = w0;
        }
      }
      dp[g][w] = best;
      back[g][w] = bestPrev;
    }
  }

  const ends = new Array<number>(G);
  let w = W;
  for (let g = G; g >= 1; g--) {
    ends[g - 1] = w;
    const prev = back[g][w];
    // Unreachable in practice (G <= W guarantees a path); degrade to an even
    // split rather than throw, because this stage must never fail a run.
    w = prev >= 0 ? prev : Math.max(g - 1, w - 1);
  }
  return ends;
}

/* ------------------------------------------------------------------ */
/* 4 — the stage                                                       */
/* ------------------------------------------------------------------ */

export interface GroupingContext {
  env: EnvelopeLike;
  bursts: readonly SpanSec[];
  onsets: readonly OnsetCandidate[];
  durationSec: number;
}

/**
 * Re-cut `segments` into burst-first groups.
 *
 * `segments` must be time-ordered — which is what `refineBoundaries` guarantees.
 * Their TIMES are used for one thing only, deciding which burst each word landed
 * in; every emitted boundary comes from the audio.
 */
export function groupByBursts(
  segments: readonly RawSegment[],
  ctx: GroupingContext,
  options: GroupingOptions = {},
): GroupingResult {
  const maxWords = options.maxWords ?? MAX_GROUP_WORDS;
  const maxGroupSec = options.maxGroupSec ?? MAX_GROUP_SEC;
  const minGroupSec = options.minGroupSec ?? MIN_GROUP_SEC;
  const splitStrength = options.splitStrength ?? ONSET_SPLIT_STRENGTH;
  const { env, bursts, onsets, durationSec } = ctx;

  const result: GroupingResult = {
    segments: [],
    bursts: [],
    multiWordGroups: 0,
    wordsFused: 0,
    emptyBursts: 0,
    confidentSplits: 0,
    forcedSplits: 0,
    capViolations: 0,
    widenedToFloor: 0,
    boundaryEvidenceFrac: 1,
  };
  if (segments.length === 0 || bursts.length === 0) return result;

  const hop = env.hopSec;
  const speechIn = (from: number, to: number): number => {
    const a = Math.max(0, Math.floor(from / hop));
    const b = Math.min(env.mask.length, Math.ceil(to / hop));
    let n = 0;
    for (let i = a; i < b; i++) if (env.mask[i]) n++;
    return n * hop;
  };

  const owners = assignToBursts(segments, bursts);
  const out: RawSegment[] = [];

  for (let b = 0; b < bursts.length; b++) {
    const burst = bursts[b];
    const [from, to] = owners[b];
    const words = segments.slice(from, to);
    const T0 = Math.max(0, burst.startSec);
    const T1 = Math.min(durationSec, burst.endSec);

    const log: BurstGroupLog = {
      burstIndex: b,
      startSec: round3(T0),
      endSec: round3(T1),
      wordsIn: words.length,
      segmentsOut: 0,
      confidentOnsets: 0,
      splits: [],
      labels: [],
      capViolations: 0,
    };

    if (words.length === 0) {
      log.note = 'no labels for this burst — speech here will stay uncovered';
      result.emptyBursts++;
      result.bursts.push(log);
      continue;
    }
    if (T1 - T0 <= 0) {
      log.note = 'empty burst span';
      result.bursts.push(log);
      continue;
    }

    /* -- candidates ------------------------------------------------- */
    const flux = onsets.filter((o) => o.timeSec > T0 && o.timeSec < T1);
    const confident = flux.filter((o) => o.strength >= splitStrength);
    log.confidentOnsets = confident.filter(
      (o) => o.timeSec >= T0 + minGroupSec && o.timeSec <= T1 - minGroupSec,
    ).length;

    /*
     * At most W-1 interior cuts, because a group must hold at least one word.
     * This is the rule that makes "the detector fires on syllables" harmless:
     * a 1.6 s burst carrying a single label cannot be divided no matter how many
     * peaks sit inside it.
     */
    const maxSplits = words.length - 1;
    const splits: SplitPoint[] = pickSplits(confident, T0, T1, minGroupSec, maxSplits).map((c) => ({
      timeSec: c.timeSec,
      strength: c.strength,
      source: 'flux',
      confident: true,
      forced: false,
    }));

    // Dips are only ever consulted to satisfy a cap, so they are computed lazily.
    let dips: OnsetCandidate[] | null = null;
    const dipList = (): OnsetCandidate[] => (dips ??= detectDips(env, T0, T1));

    /* -- caps: duration first, it needs no word assignment ----------- */
    const boundaries = (): number[] => [T0, ...splits.map((s) => s.timeSec), T1];

    const heard = words.map((w) => w.heard);
    const kinds = words.map((w) => w.kind);
    const assignEnds = (): number[] => {
      const bounds = boundaries();
      const spans: Array<{ startSec: number; endSec: number; speechSec: number }> = [];
      for (let i = 0; i < bounds.length - 1; i++) {
        spans.push({
          startSec: bounds[i],
          endSec: bounds[i + 1],
          speechSec: speechIn(bounds[i], bounds[i + 1]),
        });
      }
      return assignWordsToGroups(heard, kinds, spans, maxWords);
    };

    for (let guard = 0; guard < 16 && splits.length < maxSplits; guard++) {
      const bs = boundaries();
      let worst = -1;
      let worstDur = maxGroupSec;
      for (let i = 0; i < bs.length - 1; i++) {
        const dur = bs[i + 1] - bs[i];
        if (dur > worstDur) {
          worstDur = dur;
          worst = i;
        }
      }
      if (worst < 0) break;
      const extra = forcedSplitIn(
        flux,
        dipList(),
        bs[worst],
        bs[worst + 1],
        minGroupSec,
        splits.map((s) => s.timeSec),
      );
      if (!extra) break;
      splits.push(extra);
      splits.sort((x, y) => x.timeSec - y.timeSec);
    }

    /* -- assign words, then enforce the word cap --------------------- */
    let ends = assignEnds();

    for (let guard = 0; guard < 16 && splits.length < maxSplits; guard++) {
      const bs = boundaries();
      let over = -1;
      let start = 0;
      for (let g = 0; g < ends.length; g++) {
        if (ends[g] - start > maxWords) {
          over = g;
          break;
        }
        start = ends[g];
      }
      if (over < 0) break;
      const extra = forcedSplitIn(
        flux,
        dipList(),
        bs[over],
        bs[over + 1],
        minGroupSec,
        splits.map((s) => s.timeSec),
      );
      if (!extra) break;
      splits.push(extra);
      splits.sort((x, y) => x.timeSec - y.timeSec);
      ends = assignEnds();
    }

    /* -- a filler or noise group may not END mid-burst ----------------- */
    /*
     * The pipeline's standing rule (it is in the re-timing prompt too): an
     * elongated "ummmm" must contain the WHOLE hum, and a non-speech tile that
     * stops partway through a continuous stretch of sound is the one cut that is
     * always wrong. A group of nothing but fillers and noise is therefore only
     * allowed to be the LAST group in its burst — anywhere else it is merged
     * forward into its neighbour, which drops the split and gives the merged
     * group a real word, so its kind becomes `word` and the hum stays whole.
     * A filler alone on its own burst is untouched: it already ends at silence.
     */
    const ranges: Array<{ b0: number; b1: number; w0: number; w1: number }> = [];
    for (let g = 0, ws = 0; g < ends.length; g++) {
      ranges.push({ b0: g, b1: g + 1, w0: ws, w1: ends[g] });
      ws = ends[g];
    }
    for (let g = 0; g < ranges.length - 1; ) {
      const members = words.slice(ranges[g].w0, ranges[g].w1);
      if (members.every((m) => m.kind !== 'word')) {
        ranges[g] = { b0: ranges[g].b0, b1: ranges[g + 1].b1, w0: ranges[g].w0, w1: ranges[g + 1].w1 };
        ranges.splice(g + 1, 1);
      } else g++;
    }
    // Splits the merge above swallowed are no longer boundaries and must not be
    // counted as evidence for anything.
    const kept = new Set(ranges.slice(0, -1).map((r) => r.b1));
    for (let i = splits.length - 1; i >= 0; i--) if (!kept.has(i + 1)) splits.splice(i, 1);

    /* -- emit -------------------------------------------------------- */
    // Rebuilt from the SURVIVING splits, so group g runs bs[g]..bs[g+1] again.
    const bs = boundaries();
    for (let g = 0; g < ranges.length; g++) {
      const members = words.slice(ranges[g].w0, ranges[g].w1);
      const text = joinLabels(members.map((m) => m.heard)) || members[0]?.heard || '';
      const contextual =
        joinLabels(members.map((m) => m.contextual || m.heard)) || text;
      const kind = groupKind(members.map((m) => m.kind));

      let startSec = bs[g];
      let endSec = bs[g + 1];
      if (endSec - startSec < MIN_EMIT_SEC) {
        // Only ever happens for a whole burst shorter than the floor, so the
        // room being borrowed is silence on both sides. Clamped to the file and
        // to the neighbouring bursts so it can never reach into real speech.
        const floorBefore = b > 0 ? bursts[b - 1].endSec : 0;
        const ceilAfter = b + 1 < bursts.length ? bursts[b + 1].startSec : durationSec;
        let need = MIN_EMIT_SEC - (endSec - startSec);
        const takeBefore = Math.min(Math.max(0, startSec - floorBefore), need / 2);
        startSec -= takeBefore;
        need -= takeBefore;
        const takeAfter = Math.min(Math.max(0, ceilAfter - endSec), need);
        endSec += takeAfter;
        need -= takeAfter;
        if (need > 0) startSec -= Math.min(Math.max(0, startSec - floorBefore), need);
        result.widenedToFloor++;
      }

      out.push({
        startSec: round3(startSec),
        endSec: round3(endSec),
        heard: text,
        contextual,
        confidence: members.reduce((m, x) => Math.min(m, x.confidence), 1),
        kind,
      });

      log.labels.push(text);
      if (members.length > 1) {
        result.multiWordGroups++;
        result.wordsFused += members.length;
      }
      if (members.length > maxWords || endSec - startSec > maxGroupSec + 1e-6) {
        log.capViolations++;
        result.capViolations++;
      }
    }

    log.splits = splits.map((s) => ({ ...s, timeSec: round3(s.timeSec), strength: round3(s.strength) }));
    log.segmentsOut = ranges.length;
    for (const s of splits) {
      if (s.confident) result.confidentSplits++;
      else result.forcedSplits++;
    }
    if (log.capViolations > 0) {
      log.note = log.note
        ? `${log.note}; ${log.capViolations} group(s) still over a cap with nothing left to split on`
        : `${log.capViolations} group(s) still over a cap with nothing left to split on`;
    }
    result.bursts.push(log);
  }

  // Adjacent segments inside a burst already share a boundary VALUE; rounding is
  // applied to the same number on both sides, so they stay exactly flush. The
  // loop below is an assertion, not a repair: anything it finds is a bug here.
  for (let i = 1; i < out.length; i++) {
    if (out[i].startSec < out[i - 1].endSec) out[i].startSec = out[i - 1].endSec;
  }

  const totalBoundaries = result.confidentSplits + result.forcedSplits + result.bursts.filter((l) => l.segmentsOut > 0).length * 2;
  result.segments = out;
  result.boundaryEvidenceFrac =
    totalBoundaries > 0 ? (totalBoundaries - result.forcedSplits) / totalBoundaries : 1;
  return result;
}

export default groupByBursts;
